<template>
    <v-container class="modal-grid">
        <v-row>
            <v-row v-if="loading" class="justify-center align-center">
                <v-progress-circular :size="35"
                                    color="primary"
                                    indeterminate />
            </v-row>
            <v-row class="title">
                <v-col>
                    <span>Pesquisar fundos de investimento.</span>
                </v-col>
            </v-row>
            <v-form class="fields"
                    v-model="is_valid"
                    :disabled="loading">
                <v-row>
                    <v-col>
                        <v-text-field v-model.trim="f_code_cnpj"
                                      placeholder="Código/CNPJ"
                                      label="Código/CNPJ"
                                      @keypress="keyFilter.onlyDigits"></v-text-field>
                    </v-col>
                    <v-col>
                        <v-text-field v-model.trim="description_name"
                                      placeholder="Descrição/Nome"
                                      label="Descrição/Nome"></v-text-field>
                    </v-col>
                    <v-col>
                        <v-autocomplete v-model="entityId"
                                        :items="entities"
                                        item-text="fullName"
                                        item-value="entityId"
                                        placeholder="Emissor"
                                        label="Emissor"></v-autocomplete>
                    </v-col>
                    <v-col>
                        <v-autocomplete v-model="managerId"
                                        :items="gestores"
                                        item-text="razaoSocial"
                                        item-value="idGestor"
                                        placeholder="Gestor"
                                        label="Gestor"></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-autocomplete v-model="indexId"
                                        :items="indexadores"
                                        item-text="descricao"
                                        item-value="idIndexador"
                                        placeholder="Indexador"
                                        label="Indexador"></v-autocomplete>
                    </v-col>
                    <v-col>
                        <v-autocomplete v-model="strategyId"
                                        :items="strats"
                                        item-text="name"
                                        item-value="id"
                                        placeholder="Estratégia"
                                        label="Estratégia"></v-autocomplete>
                    </v-col>
                    <v-col>
                        <v-autocomplete v-model="refIndexId"
                                        :items="irefs"
                                        item-text="descricao"
                                        item-value="idIreferencia"
                                        placeholder="Índice de referência"
                                        label="Índice de referência"></v-autocomplete>
                    </v-col>
                    <v-col>
                        <v-autocomplete v-model="class_search"
                                        :items="anbimas"
                                        placeholder="Classe ANBIMA"
                                        label="Classe ANBIMA"></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-autocomplete v-model="type_search"
                                        :items="tGestao"
                                        placeholder="Tipo de gestão"
                                        label="Tipo de gestão"></v-autocomplete>
                    </v-col>
                    <v-col>
                        <v-autocomplete v-model="mgmt_search"
                                        :items="fGestao"
                                        placeholder="Forma de gestão"
                                        label="Forma de gestão"></v-autocomplete>
                    </v-col>
                    <v-col></v-col>
                    <v-col></v-col>
                </v-row>
                <v-row class="btn-panel">
                    <v-col cols="4">
                        <v-btn class="btn-search"
                               @click="doCleanSearch">Pesquisar</v-btn>
                    </v-col>
                    <v-col cols="4">
                        <v-btn class="btn-clean"
                               @click="clearSearch">Limpar</v-btn>
                    </v-col>
                    <v-col cols="4">
                        <v-btn class="btn-close"
                               @click="$emit('close')">Fechar</v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>&nbsp;</v-col>
                </v-row>
                <v-row class="data-table">
                    <table>
                        <caption>Resultados</caption>
                        <thead>
                            <tr>
                                <th>Código/CNPJ</th>
                                <th colspan="3">Nome</th>
                                <th>Indexador</th>
                                <th>Estratégia</th>
                                <th>Índice de refrência</th>
                                <th>Classe ANBIMA</th>
                            </tr>
                        </thead>
                        <tbody class="data-values">
                            <tr v-for="{ asset, investmentFund } in results"
                                :key="asset.idAtivo" class="data-display"
                                @click="$emit('selected', { asset, investmentFund })">
                                <td :title="idFormatter.format(1, investmentFund.cnpj)">
                                    <div>{{ idFormatter.format(1, investmentFund.cnpj) }}</div>
                                </td>
                                <td :title="asset.fullname + '\nEmissor: ' + getEntityName(asset.idEntidade) + '\nGestor: ' + getManagerName(investmentFund.idGestor)"
                                    colspan="3">
                                    <div>{{ asset.fullname }}</div>
                                </td>
                                <td :title="getIndexName(asset.idIndexador)">
                                    <div>{{ getIndexName(asset.idIndexador) }}</div>
                                </td>
                                <td :title="getStrategyName(asset.idEstrategia)">
                                    <div>{{ getStrategyName(asset.idEstrategia) }}</div>
                                </td>
                                <td :title="getIRefName(asset.idIreferencia)">
                                    <div>{{ getIRefName(asset.idIreferencia) }}</div>
                                </td>
                                <td :title="investmentFund.classeAmbima + '\nForma gest: ' + (investmentFund.formasGestao || 'N/D') + '\nTipo gest.: ' + (investmentFund.tipoGestao || 'N/D')">
                                    <div>{{ investmentFund.classeAmbima }}</div>
                                </td>
                            </tr>
                        </tbody>
                        <tbody>
                            <tr class="page-ctrls">
                                <td colspan="8">
                                    <v-row>
                                        <v-col>
                                            <v-btn :disabled="loading || noResults || isFirst"
                                                   title="Primeira página"
                                                   @click="firstPage">
                                                <v-icon icon="mdi-skip-backward">
                                                    mdi-skip-backward
                                                </v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col>
                                            <v-btn :disabled="loading || noResults || isFirst"
                                                   title="Página anterior"
                                                   @click="prevPage">
                                                <v-icon icon="mdi-skip-previous">
                                                    mdi-skip-previous
                                                </v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col>
                                            <v-btn :disabled="loading || noResults || isLast"
                                                   title="Próxima página"
                                                   @click="nextPage">
                                                <v-icon icon="mdi-skip-next">
                                                    mdi-skip-next
                                                </v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col>
                                            <v-btn :disabled="loading || noResults || isLast"
                                                   title="Última página"
                                                   @click="lastPage">
                                                <v-icon icon="mdi-skip-forward">
                                                    mdi-skip-forward
                                                </v-icon>
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr class="page-ctrls">
                                <td colspan="8">
                                    <v-row>
                                        <v-col cols="3">&nbsp;</v-col>
                                        <v-col cols="2" align-self="center">
                                            {{ rlength }} registros.
                                        </v-col>
                                        <v-col cols="2" align-self="center">
                                            <input v-model="navigation" type="number"
                                                   placeholder="Página atual"
                                                   title="Página atual" /> / {{ total }}
                                        </v-col>
                                        <v-col cols="1" align-self="center">
                                            <v-btn :disabled="loading || noResults || isParked"
                                                   autocomplete="off"
                                                   title="Ir para..."
                                                   @click="navigate">
                                                <v-icon icon="mdi-arrow-bottom-right-bold-box">
                                                    mdi-arrow-bottom-right-bold-box
                                                </v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="4"></v-col>
                                    </v-row>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </v-row>
            </v-form>
        </v-row>
    </v-container>
</template>

<style lang="css">
</style>

<style lang="css" scoped>
.modal-grid {
    position        : fixed;
    width           : 100%;
    height          : 100%;
    min-width       : 100%;
    min-height      : 100%;
    width           : 100%;
    height          : 100%;
    top             : 0;
    left            : 0;
    overflow        : hidden;
    background-color: rgba(0, 0, 0, 0.25);
    z-index         : 1000;
}
.modal-grid > .row {
    position        : fixed;
    width           : 90%;
    min-width       : 90%;
    max-width       : 90%;
    height          : 90%;
    min-height      : 90%;
    max-height      : 90%;
    top             :  5%;
    left            :  5%;
    background      : #ffffff;
    box-shadow      : -4px 0px 10px rgba(0, 0, 0, 0.05);
    z-index         : 10;
    overflow-x      : hidden;
    overflow-y      : auto;
    border          : 2px outset rgba(128,128,128,0.5);
    border-radius   : 10px;
    padding         : 1em;
    z-index         : 1001;
    font-size       : 0.75em;
}
.btn-panel .col {
    text-align: center;
}
.btn-panel .col button {
    color: white;
    font-weight: bold;
}
.btn-panel .col button.btn-search {
    background-color: rgba(0,164,0,0.75);
}
.btn-panel .col button.btn-clean {
    background-color: rgba(196,64,96,0.75);
}
.btn-panel .col button.btn-close {
    background-color: rgba(64,64,64,0.75);
}
.page-ctrls td button {
    font-weight: bold;
    font-size: 1.5rem;
}
.data-table table {
    width: 100%;
    min-width: 100%;
    text-align: center;
    font-size: 0.7rem;
}
.data-table table caption {
    font-weight: bold;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
}
.data-table table tbody .row,
.data-table table tfoot .row {
    margin: 0.1rem 0;
}
.data-table table thead tr th {
    width: 12.5%;
}
.data-table table tbody.data-values tr td > div {
    cursor: pointer;
}
.fields {
    width: 100%;
    min-width: 100%;
    margin: 0;
}
.page-ctrls > td > .row > .col > input {
    width: 60px;
    min-width: 60px;
    height: 2.25rem;
    min-height: 2.25rem;
    text-align: left;
    border-radius: 10px;
    padding: 6px;
}
.data-table .data-display > td > div {
    overflow: hidden;
    height: 1.25rem !important;
    max-height: 1.25rem !important;
    min-height: 1.25rem !important;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
input[type=number] {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>

<script lang="js">
import client               from '../../../../services/AdminService';
import idFormatter          from '@/services/IdValueFormatter';
import keyFilter            from '@/utils/filter';

const LABEL_ENTITIES        = 'assets_entities';
const LABEL_GESTORES        = 'assets_gestores';
const LABEL_IREFS           = 'assets_irefs';
const LABEL_TSREMUNER       = 'assets_tsremuneracao';
const LABEL_INDEXADORES     = 'assets_indexadores';

export default {
    data                    () {
        return              {
            client,
            loading         : false,
            is_valid        : false,

            idFormatter,
            keyFilter,

            page            : 0,
            records         : 10,
            total           : 0,
            rlength         : 0,
            results         : [],
            navigation      : 1,

            entities        : [],
            gestores        : [],
            irefs           : [],
            tsRemuneracao   : [],
            indexadores     : [],
            fGestao         : [
                'N/D',
                'PASSIVA',
                'ATIVA E NÃO-REFERENCIADA',
                'ATIVA E REFERENCIADA',
                'ENHANCED'
            ],
            tGestao         : [
                'N/D',
                'FI',
                'FIDEBICENT',
                'FIPREV'
            ],
            strats          : [
                {"id":7,"name":"N\/D"},
                {"id":1,"name":"Juros Pré (Fixo)"},
                {"id":2,"name":"Juros Pós (CDI)"},
                {"id":3,"name":"Juros Pós (Inflação)"},
                {"id":4,"name":"Renda Variável"},
                {"id":5,"name":"Câmbio"},
                {"id":6,"name":"Livre"}
            ],
            anbimas         : [
                'Fundo de Ações',
                'Fundo Cambial',
                'Fundo de Renda Fixa',
                'Fundo Multimercado',
                'Previdência Multimercado Livre'
            ],

            code_cnpj       : undefined,
            description_name: undefined,
            entityId        : undefined,
            managerId       : undefined,
            indexId         : undefined,
            strategyId      : undefined,
            refIndexId      : undefined,
            class_search    : undefined,
            mgmt_search     : undefined,
            type_search     : undefined
        }
    },
    methods                 : {
        async loadCache     () {
            const defErrMsg = 'Falha não identificada ao carregar entidades';
            this.loading = true;
            try {
                try {
                    this.entities.length = 0;
                    const prev  = this.hydrateEntities();
                    if (prev && prev.length) {
                        this.entities.push(...prev);
                    } else {
                        let ents = await this.client.getEntities();
                        if (!ents.success) {
                            this.$toast.error(ents.message || defErrMsg);
                        } else {
                            this.entities.push(...ents.content);
                            this.persistEntities(ents.content);
                        }
                    }
                } catch (e) {
                    this.$toast.error(e && e.message || defErrMsg);
                }
                try {
                    this.gestores.length = 0;
                    const antr = this.hydrateGestores();
                    if (antr && antr.length) {
                        this.gestores.push(...antr);
                    } else {
                        let gest = await this.client.getGestores();
                        if (!gest.success) {
                            this.$toast.error(gest.message || defErrMsg);
                        } else {
                            this.gestores.push(...gest.content);
                            this.persistGestores(gest.content);
                        }
                    }
                } catch (e) {
                    this.$toast.error(e && e.message || 'Falha não identificada ao carregar entidades');
                }
                try {
                    this.irefs.length = 0;
                    const pref = this.hydrateIRefs();
                    if (pref && pref.length) {
                        this.irefs.push(...pref);
                    } else {
                        let pref = await this.client.getIRefs();
                        if (!pref.success) {
                            this.$toast.error(pref.message || defErrMsg);
                        } else {
                            this.irefs.push(...pref.content);
                            this.persistIRefs(pref.content);
                        }
                    }
                } catch (e) {
                    this.$toast.error(e && e.message || 'Falha não identificada ao carregar entidades');
                }
                try {
                    this.tsRemuneracao.length = 0;
                    const tsrem = this.hydrateTsRemuner();
                    if (tsrem && tsrem.length) {
                        this.tsRemuneracao.push(...tsrem);
                    } else {
                        let tsrem = await this.client.getTsRemuneracao();
                        if (!tsrem.success) {
                            this.$toast.error(tsrem.message || defErrMsg);
                        } else {
                            this.tsRemuneracao.push(...tsrem.content);
                            this.persistTsRemuner(tsrem.content);
                        }
                    }
                } catch (e) {
                    this.$toast.error(e && e.message || 'Falha não identificada ao carregar entidades');
                }
                try {
                    this.indexadores.length = 0;
                    const idx = this.hydrateIndexadores();
                    if (idx && idx.length) {
                        this.indexadores.push(...idx);
                    } else {
                        let idx = await this.client.getIndexadores();
                        if (!idx.success) {
                            this.$toast.error(idx.message || defErrMsg);
                        } else {
                            this.indexadores.push(...idx.content);
                            this.persistIndexadores(idx.content);
                        }
                    }
                } catch (e) {
                    this.$toast.error(e && e.message || 'Falha não identificada ao carregar entidades');
                }
            } finally {
                this.loading = false;
            }
            await this.doSearch();
        },
        async doSearch      () {
            try {
                this.loading = true;
                const search = this.buildSearch();
                const resp   = await client.searchInvestmentFund(search);
                if (!resp.success) {
                    this.$toast.error(
                        resp.message || 'Falha não identificada ao realizar pesquisa.');
                } else {
                    this.results = resp.content.page;
                    this.rlength = resp.content.total;
                    this.total   = Math.ceil(resp.content.total / this.records);
                }
            } finally {
                this.loading = false;
            }
        },
        doCleanSearch       () {
            this.page       = 0;
            this.navigation = 1;
            return this.doSearch();
        },
        buildSearch         () {
            let result      = {
                pagina      : this.page,
                registros   : this.records,
            };
            if (this.code_cnpj)         { result.codigo_cnpj        = this.code_cnpj;        }
            if (this.description_name)  { result.name_description   = this.description_name; }
            if (this.entityId &&
                this.entityId > 0)      { result.emissor            = this.entityId;         }
            if (this.managerId &&
                this.managerId > 0)     { result.gestor             = this.managerId;        }
            if (this.indexId &&
                this.indexId > 0)       { result.indexadores        = [this.indexId];        }
            if (this.strategyId &&
                this.strategyId > 0)    { result.estrategias        = [this.strategyId];     }
            if (this.refIndexId &&
                this.refIndexId > 0)    { result.indices            = [this.refIndexId];     }
            if (this.class_search)      { result.classe_anbima      = this.class_search;     }
            if (this.mgmt_search)       { result.forma_gestao       = this.mgmt_search;      }
            if (this.type_search)       { result.tipo_gestao        = this.type_search;      }

            return JSON.stringify(result);
        },
        clearSearch         () {
            this.code_cnpj        = undefined;
            this.description_name = undefined;
            this.entityId         = undefined;
            this.managerId        = undefined;
            this.indexId          = undefined;
            this.strategyId       = undefined;
            this.refIndexId       = undefined;
            this.class_search     = undefined;
            this.mgmt_search      = undefined;
            this.type_search      = undefined;
        },
        lastPage            () {
            const old       = this.page;
            this.page       = this.total - 1;
            this.navigation = this.total;
            if (old !== this.page) {
                return this.doSearch();
            }
            return Promise.resolve();
        },
        nextPage            () {
            const old       = this.page;
            if (this.page < this.total - 1) { ++this.page }
            this.navigation = this.page + 1;
            if (old !== this.page) {
                return this.doSearch();
            }
            return Promise.resolve();
        },
        prevPage            () {
            const old       = this.page;
            if (this.page > 0) { --this.page; }
            this.navigation = this.page + 1;
            if (old !== this.page) {
                return this.doSearch();
            }
            return Promise.resolve();
        },
        firstPage           () {
            const old       = this.page;
            this.page       = 0;
            this.navigation = 1;
            if (old !== this.page) {
                return this.doSearch();
            }
            return Promise.resolve();
        },
        navigate            () {
            const old     = this.page;
            let offset    = this.navigation;
            if (offset < 1) { offset = 1; }
            if (offset > this.total) { offset = this.total; }
            this.navigation = offset;
            this.page       = offset - 1;
            if ((offset - 1) !== old) { return this.doSearch(); }
            return Promise.resolve();
        },
        persistEntities     (a) {
            sessionStorage.setItem(LABEL_ENTITIES, a && a.length ? JSON.stringify(a) : '[]')
        },
        hydrateEntities     () {
            const text      = sessionStorage.getItem(LABEL_ENTITIES);
            return          text && JSON.parse(text) || undefined;
        },
        persistGestores     (a) {
            sessionStorage.setItem(LABEL_GESTORES, a && a.length ? JSON.stringify(a) : '[]')
        },
        hydrateGestores     () {
            const text      = sessionStorage.getItem(LABEL_GESTORES);
            return          text && JSON.parse(text) || undefined;
        },
        persistIRefs        (a) {
            sessionStorage.setItem(LABEL_IREFS, a && a.length ? JSON.stringify(a) : '[]')
        },
        hydrateIRefs        () {
            const text      = sessionStorage.getItem(LABEL_IREFS);
            return          text && JSON.parse(text) || undefined;
        },
        persistTsRemuner    (a) {
            sessionStorage.setItem(LABEL_TSREMUNER, a && a.length ? JSON.stringify(a) : '[]')
        },
        hydrateTsRemuner    () {
            const text      = sessionStorage.getItem(LABEL_TSREMUNER);
            return          text && JSON.parse(text) || undefined;
        },
        persistIndexadores  (a) {
            sessionStorage.setItem(LABEL_INDEXADORES, a && a.length ? JSON.stringify(a) : '[]')
        },
        hydrateIndexadores  () {
            const text      = sessionStorage.getItem(LABEL_INDEXADORES);
            return          text && JSON.parse(text) || undefined;
        },
        getEntityName       (id) {
            const match = this.entities.filter((e) => e.entityId === id);
            return match.length && match[0].fullName || 'N/D';
        },
        getManagerName      (id) {
            const match = this.gestores.filter((e) => e.idGestor === id);
            return match.length && match[0].razaoSocial || 'N/D';
        },
        getIndexName        (id) {
            const match = this.indexadores.filter((e) => e.idIndexador === id);
            return match.length && match[0].descricao || 'N/D';
        },
        getStrategyName     (id) {
            const match = this.strats.filter((e) => e.id === id);
            return match.length && match[0].name || 'N/D';
        },
        getIRefName         (id) {
            const match = this.irefs.filter((e) => e.idIreferencia === id);
            return match.length && match[0].descricao || 'N/D';
        }
    },
    computed                : {
        noResults           : {
            get : function () { return !(this.results && this.results.length); }
        },
        isFirst             : {
            get : function () { return this.page === 0; }
        },
        isLast              : {
            get : function () {
                return (+this.navigation) >= this.total;
            }
        },
        isParked            : {
            get : function () {
                return (+this.navigation) === (this.page + 1);
            }
        },
        f_code_cnpj         : {
            get : function () {
                return this.idFormatter.format(1, this.code_cnpj);
            },
            set : function (v) {
                this.code_cnpj = this.idFormatter.clear(1, v);
            }
        }
    },
    created                 () {
    },
    mounted                 () {
        return this.loadCache();
    }
}
</script>